import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { TransactionDto } from "@/features/wallet/api/dto";

/**
 * Marque une transaction en échec
 * (`POST /Transactions/{id}/mark-failed?reason=...` — motif en query param,
 * guide §5.3/§6.3) : à utiliser À LA PLACE de confirm-ledger si la
 * comptabilisation a échoué — libère le blocage sans mouvement de solde et
 * passe la transaction en FAILED. Appel explicite back-office (Kafka non
 * câblé, rôle flashpay-service / flashpay-admin / flashpay-superadmin).
 */
export const useMarkFailed = (transactionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "mark-failed", transactionId],
    mutationFn: ({ reason }: { reason: string }) =>
      mutateData({
        url: `${walletEndpoints.transactions.markFailed(transactionId)}?reason=${encodeURIComponent(reason)}`,
        method: "POST",
      }) as Promise<TransactionDto>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(transactionId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useMarkFailed failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
